import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrandAddUpdateModal, Filter, Header } from "./component";
import { DataTable, DeleteModal, Excel } from "CommonComponent";
import { Link, useLocation } from "react-router-dom";
import {
  fetchBrands,
  deleteBrand,
  getBrandById,
  exportBrands,
} from "pages/AdminManage/actions";
import AccessRights from "utils/AccessRights";
import "./Brands.css";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const Brands = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const brandState = useSelector((s) => s.brandState);
  const session = useSelector((s) => s.session);

  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [extraFiltersState, setExtraFiltersState] = useState({
    values: {},
  });

  // Load brands initially
  useEffect(() => {
    if (
      session.current_page_permissions &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      dispatch(fetchBrands({ ...extraFiltersState.values, object_viewed_id }));
    }
  }, [session.current_page_permissions]);

  // Reload list when redirected
  useEffect(() => {
    if (
      brandState.redirectToList &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      dispatch(fetchBrands({ ...extraFiltersState.values, object_viewed_id }));
    }
  }, [brandState.redirectToList, session.current_page_permissions]);

  useEffect(() => {
    if (!brandState.showUpdateForm) {
      setIsEdit(false);
      setShowAddUpdateModal(false);
    }
  }, [brandState.showUpdateForm]);

  // ---------- CRUD ACTIONS ----------

  const handleAdd = () => {
    setIsEdit(false);
    setShowAddUpdateModal(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setIsEdit(true);
    setShowAddUpdateModal(true);
    dispatch(getBrandById(row.id, "update", object_viewed_id));
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!selectedRow) return;
    dispatch(deleteBrand(selectedRow.id, object_viewed_id));
    setShowDeleteModal(false);
  };

  // ---------- ACTION ICONS ----------

  const getActions = (row) => (
    <div
      className="actionClass"
      style={{ textAlign: "center", whiteSpace: "nowrap" }}
    >
      {AccessRights(session.current_page_permissions, "edit", row.created_by) &&
        row.is_deleted === "n" && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-edit-${row.id}`}>Edit</Tooltip>}
          >
            <img
              src="/assets/images/edit-icon.svg"
              alt="Edit"
              onClick={() => handleEdit(row)}
              style={{ cursor: "pointer", marginRight: "10px" }}
            />
          </OverlayTrigger>
        )}

      {AccessRights(
        session.current_page_permissions,
        "delete",
        row.created_by,
      ) &&
        row.is_deleted === "n" && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-delete-${row.id}`}>Delete</Tooltip>}
          >
            <img
              src="/assets/images/delete-icon.svg"
              alt="Delete"
              onClick={() => handleDelete(row)}
              style={{ cursor: "pointer" }}
            />
          </OverlayTrigger>
        )}
    </div>
  );

  // ---------- TABLE COLUMNS ----------

  const columns = useMemo(
    () => [
      {
        id: "brand_name",
        header: "Brand Name",
        accessor: (row) => row.brand_name,
        sortable: true,
        searchable: true,
      },
      {
        id: "program_name",
        header: "Program",
        accessor: (row) => row.program?.program_name || "-",
        sortable: true,
        searchable: true,
      },
      {
        id: "actions",
        header: "Actions",
        accessor: null,
        cell: (_, row) => getActions(row),
        sortable: false,
        searchable: false,
      },
    ],
    [session.current_page_permissions],
  );

  const rows = brandState.brandsList || [];

  // ---------- FILTER + EXPORT ----------

  const filterTable = () => {
    dispatch(fetchBrands({ ...extraFiltersState.values, object_viewed_id }));
  };

  const handleExport = () => {
    dispatch(exportBrands(extraFiltersState.values, object_viewed_id));
  };

  // ---------- RENDER ----------

  return (
    <div className="wrapper-div">
      <Header
        onAdd={handleAdd}
        extraFilters={extraFiltersState.values}
        object_viewed_id={object_viewed_id}
      />

      <div className="d-flex justify-content-end align-items-center pb-4 px-4 position-sticky top-70 bg-white z-1">
        <div className="d-flex align-items-center gap-3">
          <Excel handleExport={handleExport} />
        </div>
      </div>

      <div>
        <Filter
          onFilter={filterTable}
          extraFiltersState={extraFiltersState}
          setExtraFiltersState={setExtraFiltersState}
        />
        <DataTable
          columns={columns}
          data={rows}
          serverSide={false}
          showSearch={false}
          className="table-container"
        />
      </div>

      {showAddUpdateModal && (
        <BrandAddUpdateModal
          show={showAddUpdateModal}
          handleClose={() => setShowAddUpdateModal(false)}
          isEdit={isEdit}
          object_viewed_id={object_viewed_id}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={confirmDelete}
          message="Are you sure you want to delete this brand?"
        />
      )}
    </div>
  );
};

export default Brands;
